/**
 * IndustryTemplateSelector - Allows selecting an industry template during discovery.
 *
 * Pre-loads categories for vendor classification accuracy.
 * Selection is stored locally and applied on commit.
 */

import { Factory } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface IndustryTemplateSelectorProps {
  value: string;
  onChange: (slug: string) => void;
}

export function IndustryTemplateSelector({
  value,
  onChange,
}: IndustryTemplateSelectorProps) {
  const { data: templates, isLoading } =
    trpc.classification.listTemplates.useQuery();

  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Factory className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Industry Template</Label>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Selecting an industry pre-loads common expense categories and vendor
        patterns, improving classification accuracy.
      </p>
      <Select value={value} onValueChange={onChange} disabled={isLoading}>
        <SelectTrigger className="w-full">
          <SelectValue
            placeholder={isLoading ? "Loading..." : "Select industry..."}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__skip__">Skip / Other</SelectItem>
          {templates?.map(t => (
            <SelectItem key={t.slug} value={t.slug}>
              <span className="flex items-center gap-2">
                <span>{t.name}</span>
                {t.nameGerman && (
                  <span className="text-xs text-muted-foreground">
                    ({t.nameGerman})
                  </span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
